import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "";

// The publishable key now comes from the SERVER (GET /api/stripe/config), not
// from the bundle: an admin flips Stripe between test and live at runtime
// (AdminConfig → Stripe mode) and the card form has to follow without a
// frontend rebuild. REACT_APP_STRIPE_PUBLISHABLE_KEY is kept only as a fallback
// for when that request fails, so a network blip degrades to the old behavior
// instead of an unusable checkout.
const buildTimeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

// Publishable keys are prefixed pk_test_/pk_live_ — safe to branch on
// client-side, unlike the secret key.
const modeOfKey = (k) => (String(k).startsWith("pk_test_") ? "test" : k ? "live" : null);

// One request per page load, shared by every caller.
let configPromise = null;
export function fetchStripeConfig() {
  if (!configPromise) {
    configPromise = fetch(`${API_BASE}/api/stripe/config`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .catch(() => ({
        mode: modeOfKey(buildTimeKey),
        publishableKey: buildTimeKey,
        configured: !!buildTimeKey,
        stale: true, // served from the baked-in key, mode may be out of date
      }));
  }
  return configPromise;
}

// Elements accepts a promise, so the key fetch adds no loading state of its own.
let stripeJsPromise = null;
export function getStripePromise() {
  if (!stripeJsPromise) {
    stripeJsPromise = fetchStripeConfig().then((cfg) =>
      cfg.publishableKey ? loadStripe(cfg.publishableKey) : null
    );
  }
  return stripeJsPromise;
}

// { loading, configured, mode } for UI that has to wait on the real answer —
// e.g. don't claim "payments aren't set up" before the config has arrived.
export function useStripeConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchStripeConfig().then((value) => {
      if (!cancelled) setCfg(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return {
    loading: cfg === null,
    configured: !!cfg?.configured,
    mode: cfg?.mode || null,
    testMode: cfg?.mode === "test",
  };
}
